import { Component, Input, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef, EventEmitter, Output, ViewChild, ElementRef, AfterViewInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';

import { BaseChartDirective } from 'ng2-charts';
import {
  Chart as ChartJS,
  ChartConfiguration,
  ChartType,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { ModalService } from '../../../../shared/services/modal.service';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { WidgetEdit } from '../../../../shared/components/widget-edit/widget-edit';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  LineController,
  Title,
  Tooltip,
  Legend
);

@Component({
  selector: 'app-net-worth-trend-widget',
  standalone: true,
  imports: [CommonModule, BaseChartDirective, WidgetEdit, CdkOverlayOrigin],
  templateUrl: './net-worth-trend-widget.html',
  styleUrl: './net-worth-trend-widget.scss'
})
export class NetWorthTrendWidgetComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @Input() currentValue: string = '₹25.5L';
  @Input() trendValue: string = '+$12.5L';
  @Input() trendLabel: string = 'THIS MONTH';
  @Input() chartData: number[] = [70, 80, 65, 75, 60, 70, 85, 75, 80, 70, 85, 90, 80, 85, 95, 90];
  @Input() size: 'small' | 'medium' | 'half' | 'full' = 'small';
  @Input() isEditMode: boolean = false;
  @Input() isAddMode = false;
  @Output() addWidget = new EventEmitter<void>();
  @Input() index = 0;
  @Input() activeDropdownIndex: number | null = null;
  @Input() getAvailableSizes!: (index: number) => Array<{ value: string; label: string }>;
  @Input() toggleSizeDropdown!: (index: number, event: Event) => void;
  @Input() changeWidgetSize!: (index: number, size: any) => void;
  @Input() removeWidget!: (index: number) => void;
  @Input() title = 'Net Worth Trend';

  @ViewChild(BaseChartDirective) chart?: BaseChartDirective;
  private resizeObserver: ResizeObserver | null = null;

  public lineChartData: ChartConfiguration['data'] = {
    datasets: [
      {
        data: this.chartData,
        borderColor: '#26BC8F',
        backgroundColor: 'transparent',
        borderWidth: 2,
        tension: 0.4,
        pointRadius: 0,
        pointHoverRadius: 0,
      }
    ],
    labels: []
  };

  public lineChartOptions: ChartConfiguration['options'] = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      }
    },
    scales: {
      x: {
        display: false,
        grid: {
          display: false
        }
      },
      y: {
        display: false,
        grid: {
          display: false
        }
      }
    },
    elements: {
      line: {
        borderWidth: 2
      }
    }
  };

  public lineChartType: ChartType = 'line';

  constructor(
    private modalService: ModalService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef
  ) { }

  ngOnInit() {
    // Update chart data if provided via input
    this.lineChartData.datasets[0].data = this.chartData;

    // Generate labels for each data point (required for chart to render)
    this.lineChartData.labels = this.chartData.map((_, index) => `${index}`);
  }
  /*start of code*/
  ngOnChanges(changes: SimpleChanges) {
    // Detect when size changes
    if (changes['size']) {
      const currentSize = changes['size'].currentValue;

      // Apply max-height after view is initialized
      setTimeout(() => {
        if (currentSize === 'small') {
          this.applySmallSizeConstraint();
          this.updateChartHeight();
        } else {
          // Remove max-height when toggling to other sizes (medium, half, full)
          this.removeSmallSizeConstraint();
          this.updateChartHeight();
        }
      }, 0);
    }
  }

  private applySmallSizeConstraint() {
    const containerElement = this.elementRef.nativeElement as HTMLElement;
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;

    if (widgetElement) {
      // Check if widget has customHeight (drag-resized)
      const hasCustomHeight = widgetElement.style.height && widgetElement.style.height !== '';

      if (!hasCustomHeight) {
        // Apply max-height for toggled small size (not drag-resized)
        widgetElement.style.maxHeight = '226px';
      }
    }
  }

  private removeSmallSizeConstraint() {
    const containerElement = this.elementRef.nativeElement as HTMLElement;
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;

    if (widgetElement && widgetElement.style.maxHeight) {
      // Remove max-height when toggling to medium/half/full
      widgetElement.style.maxHeight = '';
    }
  }
  /*end of code*/

  ngAfterViewInit() {
    this.setupResizeObserver();
  }

  private setupResizeObserver() {
    // Find the parent article.widget element
    const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;

    if (!widgetElement) {
      if (!this.isAddMode) {
        console.warn('Could not find parent widget element');
      }
      return;
    }

    // Set initial height after view is ready
    setTimeout(() => {
      this.updateChartHeight();
      if (this.chart) {
        this.chart.chart?.resize();
      }
    }, 0);

    this.resizeObserver = new ResizeObserver((entries) => {
      window.requestAnimationFrame(() => {
        if (!this.resizeObserver) return;
        const isDragging = document.body.classList.contains('is-resizing');
        const resizingWidgetType = document.body.dataset['resizingWidget'];

        // Only update if THIS widget type is being resized
        if (isDragging && this.chart && resizingWidgetType === 'networthtrend') {
          // Remove max-height constraint when drag-resizing
          const widgetElement = this.elementRef.nativeElement.closest('article.widget') as HTMLElement;
          if (widgetElement && widgetElement.style.maxHeight) {
            widgetElement.style.maxHeight = '';
          }

          // Update chart container height based on widget dimensions
          this.updateChartHeight();

          // Update chart when dragging
          this.chart.chart?.resize();
          this.cdr.detectChanges();
        }
      });
    });
    this.resizeObserver.observe(widgetElement);
  }

  private updateChartHeight() {
    const containerElement = this.elementRef.nativeElement as HTMLElement;
    if (!containerElement) return;

    // Get height from parent article.widget element
    const widgetElement = containerElement.closest('article.widget') as HTMLElement;
    if (!widgetElement) return;

    // Find the chart container element
    const chartContainer = containerElement.querySelector('.net-worth-widget__chart-container') as HTMLElement;
    if (!chartContainer) return;

    // Get the widget dimensions
    const widgetHeight = widgetElement.offsetHeight;
    const widgetWidth = widgetElement.offsetWidth;

    let availableHeight: number;

    // For narrow widgets (≤168px width)
    if (widgetWidth <= 168) {
      const usedHeight = 200;
      // Small narrow widgets (< 250px height): Allow shrinking below 64px
      if (widgetHeight < 250) {
        // Continue shrinking without minimum enforcement
        availableHeight = widgetHeight - usedHeight;
      } else {
        // Regular narrow widgets (≥250px): enforce 64px minimum
        availableHeight = Math.max(64, widgetHeight - usedHeight);

      }
    } else {
      // Wide widgets: use standard calculation with minimum
      const usedHeight = 150;
      availableHeight = Math.max(64, widgetHeight - usedHeight);

    }
    // Set the chart container height dynamically (overrides CSS 64px initial)
    chartContainer.style.height = `${availableHeight}px`;

    // Update chart options based on height
    this.updateChartOptions(widgetHeight);
  }

  private updateChartOptions(widgetHeight: number) {
    const minHeight = 226; // Minimum widget height

    if (widgetHeight <= minHeight) {
      // At minimum height: reset to default (no specific min/max)
      if (this.lineChartOptions?.scales?.['y']) {
        delete (this.lineChartOptions.scales['y'] as any).suggestedMin;
        delete (this.lineChartOptions.scales['y'] as any).suggestedMax;
      }
    } else {
      // Above minimum: expand Y-axis range to make line use more vertical space
      const dataValues = this.chartData;
      const dataMin = Math.min(...dataValues);
      const dataMax = Math.max(...dataValues);
      const dataRange = dataMax - dataMin;

      // Calculate height ratio to determine how much to expand
      const heightRatio = Math.min((widgetHeight - minHeight) / (400 - minHeight), 1);

      // Expand the range inversely - more height = tighter range = line appears bigger
      const expansionFactor = 1 - (heightRatio * 0.3); // 0.7 to 1.0
      const expandedRange = dataRange / expansionFactor;
      const expansion = (expandedRange - dataRange) / 2;

      if (this.lineChartOptions?.scales?.['y']) {
        (this.lineChartOptions.scales['y'] as any).suggestedMin = dataMin - expansion;
        (this.lineChartOptions.scales['y'] as any).suggestedMax = dataMax + expansion;
      }
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  openModal() {
    if (!this.isEditMode && !this.isAddMode) {
      this.modalService.openNetWorthTrendModal();
    }
    this.cdr.detectChanges();
  }
}
